import {
  useCallback, useEffect, useState,
} from 'react';
import { isPaidLicense } from '~/_shared/types/client/license.helpers';
import { copy } from '~/_shared/utils/collections/collections';
import { useStorageService } from '~/_shared/utils/storageService';
import {
  type MainMenuItem, MainMenuItems,
} from '~/mainMenu/mainMenuItem.enum';
import {
  useClientIsNew, useUserCurrentClientSelector,
} from '~/store/userData/userData.selectors';

const MENU_ITEMS_HELP_HIGHLIGHT_KEY = 'MENU_ITEMS_HELP_HIGHLIGHT';
const MENU_ITEMS_HELP_HIGHLIGHT_HIDDEN_VALUE = 'HIDDEN';

export const useMainMenuHighlightedItems = () => {
  const [highlightedMenuItems, setHighlightedMenuItems] = useState<ReadonlySet<MainMenuItem>>(new Set());
  const isClientNew = useClientIsNew();
  const storageService = useStorageService();
  const currentClient = useUserCurrentClientSelector();
  const isTrial = !isPaidLicense(currentClient?.license.type);

  const dismissHelpHighlight = useCallback((menuItem: MainMenuItem) => {
    if (menuItem !== MainMenuItems.Help) {
      return;
    }

    if (isTrial) {
      return;
    }

    setHighlightedMenuItems(prevState => copy.andRemove(prevState, [menuItem]));
    storageService.setLocalStorageItem(MENU_ITEMS_HELP_HIGHLIGHT_KEY, MENU_ITEMS_HELP_HIGHLIGHT_HIDDEN_VALUE);
  }, [isTrial, storageService]);

  useEffect(() => {
    if (isTrial) {
      setHighlightedMenuItems(prevState => copy.andAdd(prevState, [MainMenuItems.Help]));
      return;
    }

    if (!isClientNew || storageService.getLocalStorageItem(MENU_ITEMS_HELP_HIGHLIGHT_KEY) === MENU_ITEMS_HELP_HIGHLIGHT_HIDDEN_VALUE) {
      return;
    }

    setHighlightedMenuItems(prevState => copy.andAdd(prevState, [MainMenuItems.Help]));
  }, [isClientNew, isTrial, storageService]);

  return {
    highlightedMenuItems,
    dismissHelpHighlight,
  };
};
